import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Phone, PhoneIncoming, RefreshCw, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const VEHICLE_OPTIONS = ["Car / Sedan", "SUV / Pickup Truck", "Semi / Tractor-Trailer", "Box Truck / Fleet Vehicle", "RV / Camper / Motorhome", "Equipment / Machinery", "Junk Car / Scrap for Sale", "Demolition Job", "Other"];
const emptyCall = { name: "", phone: "", vehicle_type: VEHICLE_OPTIONS[0], location: "", issue: "", urgency: "emergency", source: "phone" };

const STATUSES = ["pending", "dispatched", "completed"];

const statusCls = {
  pending: "border-hazard/50 bg-hazard/10 text-hazard",
  dispatched: "border-ember/50 bg-ember/10 text-ember",
  completed: "border-emerald-500/50 bg-emerald-500/10 text-emerald-400",
  new: "border-hazard/50 bg-hazard/10 text-hazard",
  quoted: "border-ember/50 bg-ember/10 text-ember",
  closed: "border-emerald-500/50 bg-emerald-500/10 text-emerald-400",
};

const JUNK_STATUSES = ["new", "quoted", "closed"];

function formatError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Admin() {
  const [auth, setAuth] = useState(null); // null=checking, false=logged out, object=user
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [junkRequests, setJunkRequests] = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(false);
  const [filter, setFilter] = useState("all");
  const [callForm, setCallForm] = useState(null);
  const [callLoading, setCallLoading] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoadingReqs(true);
    try {
      const [towRes, junkRes] = await Promise.all([
        axios.get(`${API}/tow-requests`),
        axios.get(`${API}/junk-car-requests`),
      ]);
      setRequests(towRes.data);
      setJunkRequests(junkRes.data);
    } catch (e) {
      if (e.response?.status === 401) setAuth(false);
      else toast.error("Could not load requests.");
    } finally {
      setLoadingReqs(false);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/auth/me`).then(({ data }) => setAuth(data)).catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (auth) loadRequests();
  }, [auth, loadRequests]);

  useEffect(() => {
    if (!auth) return;
    const timer = setInterval(loadRequests, 30000);
    return () => clearInterval(timer);
  }, [auth, loadRequests]);

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setAuth(data.user);
      toast.success("Welcome back, dispatch.");
    } catch (err) {
      setLoginError(formatError(err.response?.data?.detail));
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    setAuth(false);
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/tow-requests/${id}`, { status });
      setRequests((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  const logCall = async (e) => {
    e.preventDefault();
    setCallLoading(true);
    try {
      const { data } = await axios.post(`${API}/tow-requests`, callForm);
      setRequests((rs) => [data, ...rs]);
      setCallForm(null);
      toast.success("Phone call logged.");
    } catch {
      toast.error("Could not log the call.");
    } finally {
      setCallLoading(false);
    }
  };

  const setJunkStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/junk-car-requests/${id}`, { status });
      setJunkRequests((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success(`Quote marked as ${status}.`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-ink px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-200 focus:border-ember";

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-ember" />
      </div>
    );
  }

  if (auth === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-5" data-testid="admin-login-page">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-md border border-white/10 bg-surface p-8 sm:p-10">
          <span className="flex h-10 w-10 items-center justify-center bg-ember font-display text-xl font-black text-white">F</span>
          <h1 className="mt-6 font-display text-3xl font-extrabold uppercase text-white">Dispatch Login</h1>
          <p className="mt-2 text-sm text-slate-400">Flatlander Trucking — authorized dispatch only.</p>
          <div className="mt-8 space-y-5">
            <div>
              <label htmlFor="admin-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Email</label>
              <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@flatlandertrucking.com" className={inputCls} />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Password</label>
              <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
            </div>
            {loginError && (
              <p data-testid="admin-login-error" className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400">{loginError}</p>
            )}
            <button type="submit" disabled={loginLoading} data-testid="admin-login-submit-button" className="flex w-full items-center justify-center gap-2 bg-ember px-6 py-3.5 font-display text-lg font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-emberdark disabled:opacity-60">
              {loginLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Log In
            </button>
            <a href="/" data-testid="admin-back-home-link" className="block text-center font-mono text-xs uppercase tracking-widest text-slate-500 hover:text-ember">Back to site</a>
          </div>
        </form>
      </div>
    );
  }

  const filtered = filter === "all" ? requests : requests.filter((r) => r.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: requests.filter((r) => r.status === s).length }), {});

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-surface">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-5 sm:px-8">
          <div>
            <h1 className="font-display text-2xl font-extrabold uppercase text-white">
              FlatlanderTrucking<span className="text-ember">.com</span> Dispatch
            </h1>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Signed in as {auth.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="admin-live-badge" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
              <span className="relative flex h-2 w-2">
                <span className="absolute h-full w-full animate-pulse-ring rounded-full bg-emerald-400" />
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              Live — updates every 30s
            </span>
            <button onClick={() => setCallForm({ ...emptyCall })} data-testid="admin-log-call-button" className="flex items-center gap-2 bg-ember px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors duration-200 hover:bg-emberdark">
              <PhoneIncoming className="h-3.5 w-3.5" /> Log a Call
            </button>
            <button onClick={loadRequests} data-testid="admin-refresh-button" className="flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-xs uppercase tracking-widest text-white hover:border-ember hover:text-ember">
              <RefreshCw className={`h-3.5 w-3.5 ${loadingReqs ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-xs uppercase tracking-widest text-white hover:border-red-500 hover:text-red-400">
              <LogOut className="h-3.5 w-3.5" /> Log Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        {callForm && (
          <form onSubmit={logCall} data-testid="admin-call-form" className="mb-8 grid grid-cols-1 gap-4 border border-ember/40 bg-surface p-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex items-center justify-between sm:col-span-2 lg:col-span-3">
              <h2 className="flex items-center gap-2 font-display text-xl font-bold uppercase text-white">
                <Phone className="h-5 w-5 text-ember" /> Log a Phone Call
              </h2>
              <button type="button" onClick={() => setCallForm(null)} data-testid="admin-call-cancel-button" className="text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            {[
              ["name", "Caller Name", "John Miller", "text"],
              ["phone", "Callback Phone", "(330) 555-0123", "tel"],
              ["location", "Vehicle Location", "I-76 near exit 12", "text"],
            ].map(([key, label, ph, type]) => (
              <div key={key}>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</label>
                <input
                  type={type}
                  required
                  value={callForm[key]}
                  onChange={(e) => setCallForm((f) => ({ ...f, [key]: e.target.value }))}
                  placeholder={ph}
                  data-testid={`admin-call-${key}-input`}
                  className={inputCls}
                />
              </div>
            ))}
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Vehicle Type</label>
              <select value={callForm.vehicle_type} onChange={(e) => setCallForm((f) => ({ ...f, vehicle_type: e.target.value }))} data-testid="admin-call-vehicle-select" className={inputCls}>
                {VEHICLE_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Urgency</label>
              <select value={callForm.urgency} onChange={(e) => setCallForm((f) => ({ ...f, urgency: e.target.value }))} data-testid="admin-call-urgency-select" className={inputCls}>
                <option value="emergency">Emergency — stranded now</option>
                <option value="standard">Standard — scheduled</option>
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">What They Need</label>
              <input required minLength={5} value={callForm.issue} onChange={(e) => setCallForm((f) => ({ ...f, issue: e.target.value }))} placeholder="Flat tire, needs a tow to…" data-testid="admin-call-issue-input" className={inputCls} />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <button type="submit" disabled={callLoading} data-testid="admin-call-submit-button" className="flex items-center gap-2 bg-ember px-6 py-3 font-display text-base font-bold uppercase tracking-wider text-white hover:bg-emberdark disabled:opacity-60">
                {callLoading && <Loader2 className="h-4 w-4 animate-spin" />} Log Call
              </button>
            </div>
          </form>
        )}
        <div className="flex flex-wrap gap-3" data-testid="admin-filter-bar">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`admin-filter-${s}`}
              className={`border px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors duration-200 ${
                filter === s ? "border-ember bg-ember/10 text-ember" : "border-white/15 text-slate-400 hover:text-white"
              }`}
            >
              {s} {s === "all" ? `(${requests.length})` : `(${counts[s]})`}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-4">
          {loadingReqs && requests.length === 0 ? (
            <div className="flex justify-center py-20" data-testid="admin-requests-loading"><Loader2 className="h-8 w-8 animate-spin text-ember" /></div>
          ) : filtered.length === 0 ? (
            <div className="border border-white/10 bg-surface p-16 text-center" data-testid="admin-empty-state">
              <p className="font-display text-2xl font-bold uppercase text-slate-400">No requests here yet</p>
              <p className="mt-2 text-sm text-slate-500">New tow requests from the website will land here.</p>
            </div>
          ) : (
            filtered.map((r) => (
              <article key={r.id} data-testid={`admin-request-card-${r.id.slice(0, 8)}`} className="border border-white/10 bg-surface p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display text-xl font-bold uppercase text-white">{r.name}</h3>
                      <span className={`border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${statusCls[r.status] || statusCls.pending}`} data-testid={`admin-request-status-${r.id.slice(0, 8)}`}>
                        {r.status}
                      </span>
                      {r.source === "phone" ? (
                        <span className="border border-sky-500/50 bg-sky-500/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-sky-400" data-testid={`admin-request-source-${r.id.slice(0, 8)}`}>Phone Call</span>
                      ) : (
                        <span className="border border-white/15 bg-white/5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400" data-testid={`admin-request-source-${r.id.slice(0, 8)}`}>Website</span>
                      )}
                      {r.urgency === "emergency" && (
                        <span className="border border-red-500/50 bg-red-500/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-red-400">Emergency</span>
                      )}
                    </div>
                    <p className="mt-1 font-mono text-xs text-slate-500">
                      {new Date(r.created_at).toLocaleString()} · {r.vehicle_type} · <a href={`tel:${r.phone}`} className="text-ember hover:underline" data-testid={`admin-request-phone-${r.id.slice(0, 8)}`}>{r.phone}</a>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {STATUSES.filter((s) => s !== r.status).map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatus(r.id, s)}
                        data-testid={`admin-mark-${s}-${r.id.slice(0, 8)}`}
                        className="border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-400 transition-colors duration-200 hover:border-ember hover:text-ember"
                      >
                        Mark {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                  <p className="text-slate-400"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Location — </span>{r.location}</p>
                  <p className="text-slate-400"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Issue — </span>{r.issue}</p>
                </div>
                {r.photos && r.photos.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-3" data-testid={`admin-request-photos-${r.id.slice(0, 8)}`}>
                    {r.photos.map((p, i) => (
                      <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-request-photo-${r.id.slice(0, 8)}-${i}`}>
                        <img
                          src={`${API}/files/${p}`}
                          alt={`Tow request attachment ${i + 1}`}
                          loading="lazy"
                          className="h-24 w-24 border border-white/15 object-cover transition-colors duration-200 hover:border-ember"
                        />
                      </a>
                    ))}
                  </div>
                )}
              </article>
            ))
          )}
        </div>

        <h2 className="mt-14 flex items-center gap-3 border-t border-white/10 pt-10 font-display text-2xl font-extrabold uppercase text-white" data-testid="admin-junk-heading">
          Junk Car <span className="text-ember">Quotes</span>
          <span className="font-mono text-xs font-normal tracking-widest text-slate-500">({junkRequests.length})</span>
        </h2>
        <div className="mt-6 space-y-4" data-testid="admin-junk-list">
          {junkRequests.length === 0 ? (
            <div className="border border-white/10 bg-surface p-12 text-center" data-testid="admin-junk-empty">
              <p className="font-display text-xl font-bold uppercase text-slate-400">No quote requests yet</p>
              <p className="mt-2 text-sm text-slate-500">"Sell Your Junk Car" submissions will land here.</p>
            </div>
          ) : (
            junkRequests.map((r) => (
              <article key={r.id} data-testid={`admin-junk-card-${r.id.slice(0, 8)}`} className="border border-white/10 bg-surface p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display text-xl font-bold uppercase text-white">{r.name}</h3>
                      <span className={`border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${statusCls[r.status] || statusCls.new}`} data-testid={`admin-junk-status-${r.id.slice(0, 8)}`}>
                        {r.status}
                      </span>
                      <span className="border border-white/15 bg-white/5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{r.condition}</span>
                    </div>
                    <p className="mt-1 font-mono text-xs text-slate-500">
                      {new Date(r.created_at).toLocaleString()} · {r.vehicle} · <a href={`tel:${r.phone}`} className="text-ember hover:underline" data-testid={`admin-junk-phone-${r.id.slice(0, 8)}`}>{r.phone}</a>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {JUNK_STATUSES.filter((s) => s !== r.status).map((s) => (
                      <button
                        key={s}
                        onClick={() => setJunkStatus(r.id, s)}
                        data-testid={`admin-junk-mark-${s}-${r.id.slice(0, 8)}`}
                        className="border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-400 transition-colors duration-200 hover:border-ember hover:text-ember"
                      >
                        Mark {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                  <p className="text-slate-400"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Location — </span>{r.location}</p>
                  <p className="text-slate-400"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Notes — </span>{r.notes || "None"}</p>
                </div>
                {r.photos && r.photos.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-3" data-testid={`admin-junk-photos-${r.id.slice(0, 8)}`}>
                    {r.photos.map((p, i) => (
                      <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-junk-photo-${r.id.slice(0, 8)}-${i}`}>
                        <img src={`${API}/files/${p}`} alt={`Vehicle photo ${i + 1}`} loading="lazy" className="h-24 w-24 border border-white/15 object-cover transition-colors duration-200 hover:border-ember" />
                      </a>
                    ))}
                  </div>
                )}
              </article>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
